/**
 * Custom hook for conversation data management
 * Integrates TanStack Query with Zustand store
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useCallback } from 'react';
import {
  conversationsQueryOptions,
  conversationQueryKeys,
  type ConversationParams,
  type Conversation,
} from '../api/conversations';
import {
  useConversationStore,
  useConversationLoading,
  useConversationError,
} from '../stores/conversationStore';

import { useUserSettingsStore } from '../stores/userSettingsStore';

/**
 * Hook options
 */
export interface UseConversationsOptions {
  params?: ConversationParams;
  enabled?: boolean;
  refetchOnMount?: boolean;
  refetchInterval?: number;
}

/**
 * Hook return type
 */
export interface UseConversationsResult {
  // Data
  conversations: Conversation[];
  groupedConversations: {
    pinned: Conversation[];
    today: Conversation[];
    yesterday: Conversation[];
    lastWeek: Conversation[];
    older: Conversation[];
  };
  selectedConversationId: string | null;
  
  // State
  isLoading: boolean;
  isError: boolean;
  error: string | null;
  isFetching: boolean;
  isStale: boolean;
  
  // Actions
  refetch: () => void;
  invalidate: () => void;
  setSelectedConversation: (id: string | null) => void;
}

/**
 * Main conversations hook
 * Manages fetching, caching, and state synchronization
 */
export function useConversations(options: UseConversationsOptions = {}): UseConversationsResult {
  const {
    params = {},
    enabled = true,
    refetchOnMount = true,
    refetchInterval,
  } = options;
  
  const queryClient = useQueryClient();
  
  // Get individual actions from store without using the selector hook
  const setConversationsAction = useConversationStore((state) => state.setConversations);
  const setLoadingAction = useConversationStore((state) => state.setLoading);
  const setErrorAction = useConversationStore((state) => state.setError);
  const clearErrorAction = useConversationStore((state) => state.clearError);
  const setSelectedConversationAction = useConversationStore((state) => state.setSelectedConversation);
  
  // Get state from store
  const conversations = useConversationStore((state) => state.conversations);
  const selectedConversationId = useConversationStore((state) => state.selectedConversationId);
  const searchTerm = useConversationStore((state) => state.searchTerm);
  const storeLoading = useConversationLoading();
  const storeError = useConversationError();
  
  // Memoize filtered conversations to prevent new array creation on each render
  const filteredConversations = useMemo(() => {
    if (!searchTerm.trim()) {
      return conversations;
    }
    
    const term = searchTerm.toLowerCase();
    return conversations.filter(
      (conv) =>
        conv.conversationTitle.toLowerCase().includes(term) ||
        conv.profile.toLowerCase().includes(term)
    );
  }, [conversations, searchTerm]);

  const { userSettings } = useUserSettingsStore();

  const pinnedConversationIDs = userSettings?.pinnedConversations ?? [];

  // Memoize grouped conversations to prevent new object creation on each render
  const groupedConversations = useMemo(() => {
    // Group conversations by date
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    const groups = {
      pinned: [] as Conversation[],
      today: [] as Conversation[],
      yesterday: [] as Conversation[],
      lastWeek: [] as Conversation[],
      older: [] as Conversation[],
    };

    // Sort by last accessed date (most recent first)
    const sortedConversations = [...filteredConversations].sort((a, b) => {
      const dateA = new Date(a.date_last_accesed).getTime();
      const dateB = new Date(b.date_last_accesed).getTime();
      return dateB - dateA;
    });

    // Remove pinned conversations from the filtered list
    const pinnedConversations = sortedConversations.filter(conv =>
      pinnedConversationIDs.includes(conv.conversationID)
    );
    // Exclude pinned conversations from other groups
    const nonPinnedConversations = sortedConversations.filter(conv =>
      !pinnedConversationIDs.includes(conv.conversationID)
    );
    groups.pinned = pinnedConversations;


    nonPinnedConversations.forEach((conversation) => {
      const accessDate = new Date(conversation.date_last_accesed);
      
      if (accessDate >= today) {
        groups.today.push(conversation);
      } else if (accessDate >= yesterday) {
        groups.yesterday.push(conversation);
      } else if (accessDate >= lastWeek) {
        groups.lastWeek.push(conversation);
      } else {
        groups.older.push(conversation);
      }
    });
    
    return groups;
  }, [filteredConversations, pinnedConversationIDs]);
  
  // TanStack Query for server state
  const query = useQuery({
    ...conversationsQueryOptions(params),
    enabled,
    refetchOnMount,
    refetchInterval,
  });
  
  // Memoize store action callbacks to stabilize useEffect dependencies
  const setConversations = useCallback((conversations: Conversation[]) => {
    setConversationsAction(conversations);
  }, [setConversationsAction]);
  
  const setLoading = useCallback((loading: boolean) => {
    setLoadingAction(loading);
  }, [setLoadingAction]);
  
  const setError = useCallback((error: string | null) => {
    setErrorAction(error);
  }, [setErrorAction]);
  
  const clearError = useCallback(() => {
    clearErrorAction();
  }, [clearErrorAction]);
  
  // Sync query data with store
  useEffect(() => {
    if (query.data && !query.isLoading) {
      setConversations(query.data);
      setLoading(false);
      clearError();
    }
  }, [query.data, query.isLoading, setConversations, setLoading, clearError]);
  
  // Sync query loading state with store
  useEffect(() => {
    setLoading(query.isLoading || query.isFetching);
  }, [query.isLoading, query.isFetching, setLoading]);
  
  // Sync query error state with store
  useEffect(() => {
    if (query.error) {
      const errorMessage = query.error instanceof Error 
        ? query.error.message 
        : 'Failed to fetch conversations';
      setError(errorMessage);
    } else {
      clearError();
    }
  }, [query.error, setError, clearError]);
  
  // Actions - memoized to prevent unnecessary re-renders
  const refetch = useCallback(() => {
    query.refetch();
  }, [query]);
  
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ 
      queryKey: conversationQueryKeys.lists() 
    });
  }, [queryClient]);
  
  const setSelectedConversation = useCallback((id: string | null) => {
    setSelectedConversationAction(id);
  }, [setSelectedConversationAction]);
  
  return {
    // Data
    conversations,
    groupedConversations,
    selectedConversationId,
    
    // State - combine query and store state
    isLoading: query.isLoading || storeLoading,
    isError: query.isError || !!storeError,
    error: storeError || (query.error instanceof Error ? query.error.message : null),
    isFetching: query.isFetching,
    isStale: query.isStale,
    
    // Actions
    refetch,
    invalidate,
    setSelectedConversation,
  };
}

/**
 * Hook for conversation search
 */
export function useConversationSearch() {
  const conversations = useConversationStore((state) => state.conversations);
  const searchTerm = useConversationStore((state) => state.searchTerm);
  const setSearchTerm = useConversationStore((state) => state.setSearchTerm);
  const clearSearch = useConversationStore((state) => state.clearSearch);
  
  // Memoize filtered conversations to prevent new array creation
  const filteredConversations = useMemo(() => {
    if (!searchTerm.trim()) {
      return conversations;
    }
    
    const term = searchTerm.toLowerCase();
    return conversations.filter(
      (conv) =>
        conv.conversationTitle.toLowerCase().includes(term) ||
        conv.profile.toLowerCase().includes(term)
    );
  }, [conversations, searchTerm]);
  
  return {
    searchTerm,
    setSearchTerm,
    clearSearch,
    filteredConversations,
    hasSearch: searchTerm.length > 0,
  };
}

/**
 * Hook for navbar expansion state
 */
export function useNavbarExpansion() {
  const isExpanded = useConversationStore((state) => state.isExpanded);
  const setExpanded = useConversationStore((state) => state.setExpanded);
  const toggleExpanded = useConversationStore((state) => state.toggleExpanded);
  const isNavBarLocked = useConversationStore((state) => state.isNavBarLocked);
  const setNavBarLocked = useConversationStore((state) => state.setNavBarLocked);
  
  return {
    isExpanded,
    setExpanded,
    toggleExpanded,
    isNavBarLocked,
    setNavBarLocked,
  };
}

/**
 * Hook for conversation selection
 */
export function useConversationSelection() {
  const conversations = useConversationStore((state) => state.conversations);
  const selectedConversationId = useConversationStore((state) => state.selectedConversationId);
  const setSelectedConversation = useConversationStore((state) => state.setSelectedConversation);
  
  // Memoize selected conversation to prevent unnecessary recalculation
  const selectedConversation = useMemo(() => {
    return selectedConversationId 
      ? conversations.find((conv) => conv.conversationID === selectedConversationId) || null
      : null;
  }, [conversations, selectedConversationId]);
  
  return {
    selectedConversationId,
    selectedConversation,
    setSelectedConversation,
    hasSelection: !!selectedConversationId,
  };
}

/**
 * Hook for optimistic updates
 */
export function useConversationMutations() {
  const queryClient = useQueryClient();
  
  // Get individual actions from store without using the selector hook
  const addConversationAction = useConversationStore((state) => state.addConversation);
  const updateConversationAction = useConversationStore((state) => state.updateConversation);
  const removeConversationAction = useConversationStore((state) => state.removeConversation);
  
  const addConversationOptimistic = useCallback((conversation: Conversation) => {
    // Update store immediately
    addConversationAction(conversation);
    
    // Invalidate queries to refetch from server
    queryClient.invalidateQueries({ 
      queryKey: conversationQueryKeys.lists() 
    });
  }, [addConversationAction, queryClient]);
  
  const updateConversationOptimistic = useCallback((
    id: string, 
    updates: Partial<Conversation>
  ) => {
    // Update store immediately
    updateConversationAction(id, updates);
    
    // Invalidate queries to refetch from server
    queryClient.invalidateQueries({ 
      queryKey: conversationQueryKeys.lists() 
    });
  }, [updateConversationAction, queryClient]);
  
  const removeConversationOptimistic = useCallback((id: string) => {
    // Update store immediately
    removeConversationAction(id);
    
    // Invalidate queries to refetch from server
    queryClient.invalidateQueries({ 
      queryKey: conversationQueryKeys.lists() 
    });
  }, [removeConversationAction, queryClient]);
  
  return {
    addConversationOptimistic,
    updateConversationOptimistic,
    removeConversationOptimistic,
  };
}

/**
 * Hook for cache management
 */
export function useConversationCache() {
  const queryClient = useQueryClient();
  const shouldRefetch = useConversationStore((state) => state.shouldRefetch);
  const markFetched = useConversationStore((state) => state.markFetched);
  const reset = useConversationStore((state) => state.reset);
  
  const clearCache = () => {
    queryClient.removeQueries({ 
      queryKey: conversationQueryKeys.all 
    });
    reset();
  };
  
  const refreshCache = () => {
    queryClient.invalidateQueries({ 
      queryKey: conversationQueryKeys.lists() 
    });
  };
  
  const prefetchConversations = (params: ConversationParams = {}) => {
    queryClient.prefetchQuery(conversationsQueryOptions(params));
  };
  
  return {
    shouldRefetch,
    markFetched,
    clearCache,
    refreshCache,
    prefetchConversations,
  };
}
